import { useCallback, useEffect, useRef, useState } from 'react';
import type { HubConnection } from '@microsoft/signalr';
import {
  buildTeleconsultStaffChatHubConnection,
  canonicalChatVisitUlid,
  getTeleconsultStaffChatHubUrl,
} from '../realtime/teleconsultStaffChatHub';

const PATIENT_READY_TOAST_AUTO_DISMISS_MS = 10 * 60 * 1000;

export type PatientReadyMeetToast = {
  key: string;
  visitUlid: string;
  patientName: string;
};

/** SignalR + toast state when assigned patients join Google Meet (doctor portal). */
export function useDoctorPatientReadyMeetHub(doctorId: number | null | undefined) {
  const [toasts, setToasts] = useState<PatientReadyMeetToast[]>([]);
  const timersRef = useRef<Map<string, number>>(new Map());
  const hubRef = useRef<HubConnection | null>(null);

  const dismissToast = useCallback((key: string) => {
    const timer = timersRef.current.get(key);
    if (timer != null) {
      window.clearTimeout(timer);
      timersRef.current.delete(key);
    }
    setToasts((prev) => prev.filter((t) => t.key !== key));
  }, []);

  const showToast = useCallback(
    (visitUlid: string, patientName: string) => {
      const ulid = canonicalChatVisitUlid(visitUlid);
      if (!ulid) return;
      const key = `${ulid}-${Date.now()}`;
      const name = patientName.trim() || 'Patient';
      setToasts((prev) => [...prev, { key, visitUlid: ulid, patientName: name }]);
      const timer = window.setTimeout(() => dismissToast(key), PATIENT_READY_TOAST_AUTO_DISMISS_MS);
      timersRef.current.set(key, timer);
    },
    [dismissToast],
  );

  useEffect(
    () => () => {
      timersRef.current.forEach((timer) => {
        window.clearTimeout(timer);
      });
      timersRef.current.clear();
    },
    [],
  );

  useEffect(() => {
    if (doctorId == null || doctorId <= 0) return;

    const hubUrl = getTeleconsultStaffChatHubUrl();
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('accessToken') : null;
    const conn = buildTeleconsultStaffChatHubConnection({
      hubUrl,
      accessToken: token,
      onStaffChatUpdated: () => {},
      onPatientReadyForMeet: (payload) => {
        if (typeof payload.visitUlid !== 'string' || typeof payload.patientName !== 'string') return;
        showToast(payload.visitUlid, payload.patientName);
      },
    });

    const joinDoctorQueue = async () => {
      try {
        await conn.invoke('JoinDoctorQueue', doctorId);
      } catch {
        /* hub may still be negotiating */
      }
    };

    conn.onreconnected(() => {
      void joinDoctorQueue();
    });

    hubRef.current = conn;
    let cancelled = false;

    void (async () => {
      try {
        await conn.start();
        if (cancelled) return;
        await joinDoctorQueue();
      } catch (e) {
        console.warn('[SignalR doctor Meet alerts]', e);
      }
    })();

    return () => {
      cancelled = true;
      void conn.stop();
      if (hubRef.current === conn) {
        hubRef.current = null;
      }
    };
  }, [doctorId, showToast]);

  return { toasts, dismissToast };
}
